import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronUp, faChevronRight, faChevronDown, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useInterval } from 'app/hooks/useInterval';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { JobCard } from '../JobCard/index'
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  InferJobsContainer,
  InferJobsHeader,
  InferJobsHeaderTitle,
  UpgradeLink,
  InferenceCreditsCount,
  SearchContainer,
  TabsBar,
  TabButton,
  FixedCollapseHistoryButton,
  FixedHistoryFooterMobile,
  FixedHistoryFooterContent,
  FixedHistoryFooterTitle,
  LoadingContainer,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateDescription,
  SearchTextField,
  ClearSearchButton,
  InstructionsContainer,
  InstructionsList,
  JobsListContainer,
} from './RecentJobsSidepanel.styles';
import { useRecentJobsPanel } from './hooks/useRecentJobsPanel';

interface RecentJobsSidepanelProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onJobPlayed?: (job: any) => void;
  Api: any;
  inferenceCredits?: number;
  isCreator?: boolean;
  isStarter?: boolean;
  navigate: (path: string) => void;
}

export function RecentJobsSidepanel({
  isOpen,
  onOpenChange,
  onJobPlayed,
  Api,
  inferenceCredits,
  isCreator,
  isStarter,
  navigate,
}: RecentJobsSidepanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    selectedTab,
    searchQuery,
    isPolling,
    jobs,
    isLoading,
    tabConfig,
    defaultTab,
    isPanelOpen,
    filteredTabs,
    setSelectedTab,
    setSearchQuery,
    setPolling,
    setPanelOpen,
    fetchJobs,
  } = useRecentJobsPanel();

  useEffect(() => {
    setSelectedTab(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, isPanelOpen]);

useEffect(() => {
  if (isOpen && tabConfig && jobs === null) {
    fetchJobs(Api);
  }
}, [selectedTab, isOpen, tabConfig, Api, fetchJobs, jobs]);

  useEffect(() => {
    setPanelOpen(isOpen);
  }, [isOpen, setPanelOpen]);

  const handleOpenChange = (open: boolean) => {
    setPanelOpen(open);
    onOpenChange(open);
  };

  useInterval(async () => {
    // if we are polling, fetch all recent jobs again
    if (!jobs) return;
    if (isPolling) fetchJobs(Api, true, false);

    // check for not completed jobs
    for (const job of jobs) {
      if (job && job.status !== 'COMPLETED' && job.status !== 'FAILED') {
        if (!isPolling) {
          setPolling(true);
        }
        return;
      }
    }
    // If we get here all jobs are completed, also fetch updated user profile with new inferenece credit count
    setPolling(false);
  }, 10000);

  // Filtering logic, same as InferPage
  const filteredJobs = jobs?.filter((job: any) => {
    const searchLower = searchQuery?.toLowerCase();
    // You can customize filtering by type if needed
    if (selectedTab === 'stem-splitter') {
      const stemName = job.stem_name?.toLowerCase() || '';
      if (stemName.includes(searchLower)) return true;
    }
    // General: search in voice_model, input/output filenames
    const voiceName = job.voice_model?.name?.toLowerCase() || '';
    if (voiceName.includes(searchLower)) return true;
    const inputMatches = job.audio_file_inputs?.some(
      (input: any) => input.audio_file?.filename?.toLowerCase().includes(searchLower)
    );
    if (inputMatches) return true;
    const outputMatches = job.audio_file_outputs?.some(
      (output: any) => output.audio_file?.filename?.toLowerCase().includes(searchLower)
    );
    if (outputMatches) return true;
    return false;
  });

  // If no tabConfig, render nothing
  if (!tabConfig) return null;

  // Render identical to current layout, but with horizontal tabs between search and jobs list
  return (
    <>
      {isOpen && (
        <InferJobsContainer>
          <InferJobsHeader>
            {!isMobile && (
              <IconButton onClick={() => handleOpenChange(false)}>
                <FontAwesomeIcon icon={faChevronRight} />
              </IconButton>
            )}
            <InferJobsHeaderTitle>
              <h5>My Library</h5>
              {!isCreator && !isStarter && (
                <>
                  <InferenceCreditsCount>
                    {inferenceCredits}
                    {' secs left'}
                  </InferenceCreditsCount>
                  <UpgradeLink to={tabConfig.upgradePath}>
                    {tabConfig.upgradeText}
                  </UpgradeLink>
                </>
              )}
              {isStarter && (
                <>
                  <InferenceCreditsCount>
                    {inferenceCredits}
                    {' secs left today'}
                  </InferenceCreditsCount>
                  <UpgradeLink to={tabConfig.upgradePath}>
                    Upgrade to Creator for Unlimited Swaps
                  </UpgradeLink>
                </>
              )}
            </InferJobsHeaderTitle>
            {!isMobile && (
              <Button
                onClick={() => navigate(tabConfig.viewAllPath)}
                size={'small'}
                variant={'text'}
              >
                View All
              </Button>
            )}
            {isMobile && (
              <IconButton onClick={() => handleOpenChange(false)}>
                <FontAwesomeIcon icon={faChevronDown} />
              </IconButton>
            )}
          </InferJobsHeader>
          {/* Search bar */}
          <SearchContainer>
            <SearchTextField
              fullWidth
              size="small"
              placeholder={tabConfig.placeholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faSearch} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <ClearSearchButton
                      onClick={() => setSearchQuery('')}
                      size="small"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </ClearSearchButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </SearchContainer>
          {/* Horizontal tabs bar */}
          <TabsBar>
            {filteredTabs.map(tab => (
              <TabButton
                key={tab.key}
                selected={selectedTab === tab.key}
                onClick={() => setSelectedTab(tab.key)}
                type="button"
                disabled={isLoading}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabsBar>
          {/* Only the jobs list below this line will scroll vertically. The header, search, and tabs remain fixed. */}
          <JobsListContainer>
            {isLoading ? (
              <LoadingContainer>
                <CircularProgress size={32} sx={{ color: '#FF74E9' }} />
              </LoadingContainer>
            ) : filteredJobs?.length === 0 ? (
              searchQuery ? (
                <EmptyStateContainer>
                  <EmptyStateTitle>No results found</EmptyStateTitle>
                  <EmptyStateDescription>
                    Try adjusting your search terms
                  </EmptyStateDescription>
                </EmptyStateContainer>
              ) : selectedTab === defaultTab ? (
                <EmptyStateContainer>
                  <EmptyStateTitle>
                    You haven’t generated anything yet!
                  </EmptyStateTitle>
                  <InstructionsContainer>
                    {tabConfig.instructions && (
                      <InstructionsList>
                        {tabConfig.instructions.map(
                          (line: string, i: number) => (
                            <li key={i}>{line}</li>
                          ),
                        )}
                      </InstructionsList>
                    )}
                  </InstructionsContainer>
                  <EmptyStateDescription>
                    Your generated music will appear here
                  </EmptyStateDescription>
                </EmptyStateContainer>
              ) : (
                <EmptyStateContainer>
                  <EmptyStateTitle>
                    You haven’t generated anything yet!
                  </EmptyStateTitle>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => navigate(tabConfig.toolPath)}
                    sx={{
                      mt: '23px',
                      borderRadius: '24px',
                      background:
                        'linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%)',
                      color: '#F5E1FF',
                      fontFamily: 'Jura',
                      fontWeight: 400,
                      fontSize: 20,
                      letterSpacing: '0.46px',
                      textTransform: 'uppercase',
                      padding: '12px 36px',
                      '&:hover': {
                        background:
                          'linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%)',
                        opacity: 0.9,
                      },
                    }}
                  >
                    {`Go to ${tabConfig.label}`}
                  </Button>
                </EmptyStateContainer>
              )
            ) : (
              filteredJobs?.map((job: any) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onPlayed={onJobPlayed}
                  onAfterDeleteJob={() => null}
                />
              ))
            )}
            {/* End of scrollable jobs list */}
          </JobsListContainer>
        </InferJobsContainer>
      )}
      {/* Button to open the panel (collapsed) */}
      {!isOpen && (
        <>
          {!isMobile && (
            <FixedCollapseHistoryButton>
              <IconButton onClick={() => handleOpenChange(true)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </IconButton>
            </FixedCollapseHistoryButton>
          )}
          {isMobile && (
            <FixedHistoryFooterMobile>
              <FixedHistoryFooterContent>
                <FixedHistoryFooterTitle>
                  {tabConfig.title}
                </FixedHistoryFooterTitle>
                {!isCreator && !isStarter && (
                  <>
                    <InferenceCreditsCount>
                      {inferenceCredits}
                      {' secs left '}
                      <UpgradeLink to={tabConfig.upgradePath}>
                        {tabConfig.upgradeText}
                      </UpgradeLink>
                    </InferenceCreditsCount>
                  </>
                )}
                {isStarter && (
                  <>
                    <InferenceCreditsCount>
                      {inferenceCredits}
                      {' secs left today '}
                      <UpgradeLink to={tabConfig.upgradePath}>
                        Upgrade to Creator for Unlimited Swaps
                      </UpgradeLink>
                    </InferenceCreditsCount>
                  </>
                )}
              </FixedHistoryFooterContent>
              <IconButton onClick={() => handleOpenChange(true)}>
                <FontAwesomeIcon icon={faChevronUp} />
              </IconButton>
            </FixedHistoryFooterMobile>
          )}
        </>
      )}
    </>
  );
}
